import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConnectPlatformDeepLink } from '@/components/settings/ConnectPlatformDeepLink';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/use-auth';
import { campaignService } from '@/services/data/CampaignService';

interface Props {
  projectId: string;
  missingKeys: Array<'target_audience' | 'cadence'>;
  onSubmitted: () => void;
}

export function WorkflowBriefCaptureBody({ projectId, missingKeys, onSubmitted }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [audience, setAudience] = useState('');
  const [postsPerWeek, setPostsPerWeek] = useState(3);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const { data: connected = [] } = useQuery({
    queryKey: ['user-connections', 'platforms', user?.id],
    queryFn: () =>
      user
        ? campaignService.listConnectedPlatforms().then((r) => r.data ?? [])
        : Promise.resolve([]),
    enabled: !!user,
  });

  const submit = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      if (missingKeys.includes('target_audience')) patch.target_audience = audience;
      if (missingKeys.includes('cadence')) {
        patch.cadence = {
          posts_per_week: postsPerWeek,
          platforms: Array.from(picked),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
      }
      const resp = await campaignService.updateWorkflowBrief(projectId, patch);
      if (resp.error) throw resp.error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign', projectId] });
      onSubmitted();
    },
  });

  const togglePlatform = (p: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(p)) {
        next.delete(p);
      } else {
        next.add(p);
      }
      return next;
    });

  const isDisabled =
    submit.isPending ||
    (missingKeys.includes('target_audience') && !audience.trim()) ||
    (missingKeys.includes('cadence') && (picked.size === 0 || connected.length === 0));

  return (
    <div className="space-y-6 p-4">
      <div>
        <h2 className="text-base font-semibold">Quick context for this workflow</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Two questions and Mora can start drafting in your voice.
        </p>
      </div>

      {missingKeys.includes('target_audience') && (
        <div className="space-y-2">
          <Label htmlFor="audience">Who is this for?</Label>
          <Textarea
            id="audience"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            placeholder="e.g. solo creators monetizing newsletters"
            rows={2}
          />
        </div>
      )}

      {missingKeys.includes('cadence') && (
        <>
          <div className="space-y-2">
            <Label>Posts per week</Label>
            <div className="flex items-center gap-3">
              <Button
                variant="flat"
                size="sm"
                onClick={() => setPostsPerWeek(Math.max(1, postsPerWeek - 1))}
              >
                &minus;
              </Button>
              <span className="w-8 text-center text-base font-semibold">{postsPerWeek}</span>
              <Button
                variant="flat"
                size="sm"
                onClick={() => setPostsPerWeek(Math.min(20, postsPerWeek + 1))}
              >
                +
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Platforms</Label>
            {connected.length === 0 ? (
              <div className="bg-muted/40 border-border/60 rounded-md border p-3 text-sm">
                <p className="mb-2">You haven&apos;t connected any platforms yet.</p>
                <Button asChild variant="flat" size="sm">
                  <a href={ConnectPlatformDeepLink('instagram')}>Connect a platform &rarr;</a>
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {connected.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePlatform(p)}
                    aria-pressed={picked.has(p)}
                    className={
                      picked.has(p)
                        ? 'bg-foreground text-background rounded-md px-3 py-1 text-sm'
                        : 'bg-muted/40 hover:bg-muted/60 border-border/60 rounded-md border px-3 py-1 text-sm'
                    }
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <Button variant="neutral" onClick={() => submit.mutate()} disabled={isDisabled}>
        {submit.isPending ? 'Saving…' : 'Save and continue'}
      </Button>
    </div>
  );
}
